import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { PageHeader } from "@/components/PageHeader";
import { KpiTile } from "@/components/KpiTile";
import { StatusBadge } from "@/components/StatusBadge";
import { DataTable } from "@/components/DataTable";
import { fmtNum, fmtRelative, fmtSeconds, fmtDate, fmtCost, extractName } from "@/lib/format";
import { AreaChart, Area, ResponsiveContainer, Tooltip, XAxis, YAxis, ReferenceLine } from "recharts";
import { Shimmer } from "@/components/Shimmer";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Inbox, Calendar as CalendarIcon, X } from "lucide-react";

const WINDOWS = [
  { key: "today",     label: "Today" },
  { key: "last_24h",  label: "Last 24h" },
  { key: "last_7d",   label: "Last 7 days" },
  { key: "last_30d",  label: "Last 30 days" },
  { key: "this_quarter", label: "This quarter" },
  { key: "ytd",       label: "Year to date" },
];

function startOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

function endOfLocalDay(d) {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x;
}

/**
 * Compute window bounds in the user's LOCAL timezone, then return as UTC ISO
 * strings for the backend (which stores timestamps in UTC).
 */
function computeWindow(key, now = new Date()) {
  const end = new Date(now);
  let start;
  switch (key) {
    case "today":
      start = startOfLocalDay(now);
      break;
    case "last_24h":
      start = new Date(now.getTime() - 24 * 3600 * 1000);
      break;
    case "last_7d":
      start = new Date(now.getTime() - 7 * 24 * 3600 * 1000);
      break;
    case "last_30d":
      start = new Date(now.getTime() - 30 * 24 * 3600 * 1000);
      break;
    case "ytd":
      start = new Date(now.getFullYear(), 0, 1, 0, 0, 0, 0);
      break;
    case "this_quarter": {
      const q = Math.floor(now.getMonth() / 3);
      start = new Date(now.getFullYear(), q * 3, 1, 0, 0, 0, 0);
      break;
    }
    default:
      start = startOfLocalDay(now);
  }
  return { start, end };
}

function labelForCounts(window) {
  const m = WINDOWS.find((w) => w.key === window);
  return (m?.label || "Today").toLowerCase();
}

export default function HomePage() {
  const [window_, setWindow] = useState("today");
  const [range, setRange] = useState({ from: null, to: null });
  const [data, setData] = useState(null);
  const [err, setErr] = useState(null);
  const nav = useNavigate();

  const queryParams = useMemo(() => {
    if (range.from && range.to) {
      // User picked a local calendar range — translate to UTC ISO.
      const start = startOfLocalDay(range.from);
      const end = endOfLocalDay(range.to);
      return { date_from: start.toISOString(), date_to: end.toISOString() };
    }
    // Preset windows — compute bounds in LOCAL tz, send as UTC ISO.
    const { start, end } = computeWindow(window_);
    return { date_from: start.toISOString(), date_to: end.toISOString() };
  }, [window_, range]);

  useEffect(() => {
    let alive = true;
    const load = () =>
      api.get("/home/overview", { params: queryParams })
        .then((r) => { if (alive) setData(r.data); })
        .catch((e) => { if (alive) setErr(e.message); });
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [queryParams]);

  // Columns for the "last 10 emails" table — must be before any early return.
  const recentColumns = useMemo(() => [
    {
      accessorKey: "from_addr",
      header: "From",
      size: 200,
      cell: ({ getValue }) => <span className="truncate">{extractName(getValue())}</span>,
    },
    {
      accessorKey: "subject",
      header: "Subject",
      size: 420,
      cell: ({ getValue }) =>
        getValue()
          ? <span className="truncate">{getValue()}</span>
          : <span className="meta">(no subject)</span>,
    },
    {
      accessorKey: "category",
      header: "Category",
      size: 120,
      cell: ({ getValue }) => getValue()
        ? <span className="px-1.5 py-0.5 rounded bg-zinc-100 text-zinc-700 uppercase text-[10px] tracking-wider font-semibold">{getValue()}</span>
        : <span className="meta">—</span>,
    },
    {
      accessorKey: "status",
      header: "Status",
      size: 120,
      cell: ({ getValue }) => <StatusBadge status={getValue()} />,
    },
    {
      accessorKey: "attachment_count",
      header: "Att",
      size: 70,
      meta: { align: "center" },
      cell: ({ getValue }) => <span className="num meta">{getValue() || ""}</span>,
    },
    {
      accessorKey: "llm_cost_usd",
      header: "LLM $",
      size: 90,
      meta: { align: "right" },
      cell: ({ getValue }) => <span className="num meta">{fmtCost(getValue())}</span>,
    },
    {
      accessorKey: "ingested_at",
      header: "Received",
      size: 140,
      cell: ({ getValue }) => <span className="meta mono" style={{ fontSize: 12 }}>{fmtRelative(getValue())}</span>,
    },
  ], []);

  if (err) return <div className="p-6 text-rose-700">Error: {err}</div>;

  const hasCustom = range.from && range.to;
  const activeLabel = hasCustom
    ? `${fmtDate(range.from.toISOString())} → ${fmtDate(range.to.toISOString())}`
    : labelForCounts(window_);

  // Build a drill-down URL for emails, carrying the current window bounds.
  const emailsHref = (extra = {}) => {
    const p = new URLSearchParams();
    p.set("date_from", queryParams.date_from);
    p.set("date_to",   queryParams.date_to);
    Object.entries(extra).forEach(([k, v]) => v && p.set(k, v));
    return `/emails?${p.toString()}`;
  };

  return (
    <div className="p-8 max-w-[1600px]" data-testid="home-page">
      <PageHeader
        eyebrow="Overview"
        title="Pipeline Overview"
        subtitle={`Agent activity in the selected window. Auto-refreshes every 15s.`}
        actions={
          <div className="flex items-center gap-2">
            <Select
              value={hasCustom ? "__custom__" : window_}
              onValueChange={(v) => {
                if (v === "__custom__") return;
                setRange({ from: null, to: null });
                setWindow(v);
              }}
            >
              <SelectTrigger className="w-[160px]" data-testid="home-window-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {WINDOWS.map((w) => (
                  <SelectItem key={w.key} value={w.key}>{w.label}</SelectItem>
                ))}
                {hasCustom && <SelectItem value="__custom__">{activeLabel}</SelectItem>}
              </SelectContent>
            </Select>

            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-10 font-mono text-xs"
                  data-testid="home-range-btn"
                >
                  <CalendarIcon size={14} className="mr-1.5" />
                  {hasCustom ? activeLabel : "Custom range"}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <Calendar
                  mode="range"
                  selected={range.from ? { from: range.from, to: range.to } : undefined}
                  onSelect={(r) => {
                    if (r?.from && r?.to) {
                      setRange({ from: r.from, to: r.to });
                    } else if (r?.from) {
                      setRange({ from: r.from, to: null });
                    }
                  }}
                  numberOfMonths={2}
                  initialFocus
                  data-testid="home-range-calendar"
                />
              </PopoverContent>
            </Popover>

            {hasCustom && (
              <Button
                size="icon"
                variant="ghost"
                className="h-9 w-9"
                onClick={() => setRange({ from: null, to: null })}
                data-testid="home-range-clear"
                title="Clear custom range"
              >
                <X size={14} />
              </Button>
            )}
          </div>
        }
      />

      {/* KPI tiles — sums reconcile: received = processed + skipped + errored + pending */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 mb-2" data-testid="today-kpis">
        {data ? (
          <>
            <DrillKpi testId="kpi-received"  label={`Received · ${activeLabel}`}
                      value={fmtNum(data.today.received)} to={emailsHref()} />
            <DrillKpi testId="kpi-processed" label="Processed" valueClassName="text-emerald-700"
                      value={fmtNum(data.today.processed)} to={emailsHref({ status: "processed" })} />
            <DrillKpi testId="kpi-pending"   label="Pending"   valueClassName={data.today.pending > 0 ? "text-blue-700" : ""}
                      value={fmtNum(data.today.pending)}   to={emailsHref({ status: "pending" })} />
            <DrillKpi testId="kpi-skipped"   label="Skipped"   valueClassName={data.today.skipped > 0 ? "text-amber-700" : ""}
                      value={fmtNum(data.today.skipped)}   to={emailsHref({ status: "skipped" })} />
            <DrillKpi testId="kpi-errored"   label="Errored"   valueClassName={data.today.errored > 0 ? "text-rose-700" : ""}
                      value={fmtNum(data.today.errored)}   to={emailsHref({ status: "error" })} />
          </>
        ) : [...Array(5)].map((_, i) => <Shimmer key={i} className="h-[120px]" />)}
      </div>
      {data && (
        <p className="text-[11px] font-mono text-muted-foreground mb-6" data-testid="kpi-reconcile">
          received = processed + pending + skipped + errored &nbsp;·&nbsp;
          {data.today.received} = {data.today.processed} + {data.today.pending} + {data.today.skipped} + {data.today.errored}
        </p>
      )}

      {/* Latency + Errors row */}
      <div className="grid lg:grid-cols-3 gap-4 mb-6">
        <div className="card-surface p-4 lg:col-span-2 min-h-[240px]" data-testid="latency-card">
          <div className="flex items-baseline justify-between mb-4">
            <div>
              <div className="overline">Latency · last {data?.latency.window_hours || 24}h</div>
              <div className="font-mono text-xs text-muted-foreground mt-1">
                received → Salesforce · target 10s
              </div>
            </div>
            {data && (
              <div className="flex items-baseline gap-6 font-mono text-sm">
                <span>median <b className="text-xl">{fmtSeconds(data.latency.median)}</b></span>
                <span>p95 <b className="text-xl">{fmtSeconds(data.latency.p95)}</b></span>
                <span className="text-muted-foreground">n={data.latency.count}</span>
              </div>
            )}
          </div>
          <div className="h-[160px]">
            {data && data.latency.series.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={data.latency.series}>
                  <defs>
                    <linearGradient id="lat" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#09090B" stopOpacity={0.3} />
                      <stop offset="100%" stopColor="#09090B" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="x" hide />
                  <YAxis hide />
                  <Tooltip contentStyle={{ fontSize: 12 }} />
                  <ReferenceLine y={10} stroke="#F43F5E" strokeDasharray="3 3" label={{ value: "10s target", fontSize: 10, fill: "#F43F5E" }} />
                  <Area dataKey="y" stroke="#09090B" fill="url(#lat)" strokeWidth={1.5} />
                </AreaChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">No processed emails in this window</div>
            )}
          </div>
        </div>

        <div className="card-surface p-4 min-h-[240px]" data-testid="errors-card">
          <div className="overline mb-3 flex items-center gap-2">
            <AlertTriangle size={12} /> Errors · last 5
          </div>
          {data?.errors?.length ? (
            <ul className="divide-y divide-border text-sm">
              {data.errors.map((e) => (
                <li key={e.id} className="py-2">
                  <Link to={`/emails/${e.id}`} className="block hover:bg-zinc-50 -mx-2 px-2 py-1 rounded">
                    <div className="font-medium truncate">{e.subject || "(no subject)"}</div>
                    <div className="text-xs text-muted-foreground truncate font-mono">
                      {extractName(e.from_addr)} · {fmtRelative(e.ingested_at)}
                    </div>
                    {e.error && <div className="text-xs text-rose-700 truncate mt-1">{e.error}</div>}
                  </Link>
                </li>
              ))}
            </ul>
          ) : (
            <div className="h-full grid place-items-center text-sm text-muted-foreground">
              No errors
            </div>
          )}
        </div>
      </div>

      {/* Recent emails */}
      <DataTable
        data-testid="recent-emails-card"
        columns={recentColumns}
        data={data?.recent_emails || []}
        onRowClick={(e) => nav(`/emails/${e.id}`)}
        getRowTestId={(e) => `home-recent-${e.id}`}
        emptyMessage={data ? "No emails yet" : "Loading…"}
        maxHeight="none"
        header={
          <>
            <div className="overline flex items-center gap-2"><Inbox size={12} /> Last 10 emails</div>
            <Link to="/emails" className="text-xs mono text-zinc-600 hover:text-zinc-900">Open all →</Link>
          </>
        }
      />

      {/* Health footer */}
      {data && (
        <div className="mt-6 grid grid-cols-2 lg:grid-cols-3 gap-4" data-testid="system-health">
          <HealthCell label="Persist success" value={data.system_health.persist_success_rate} />
          <HealthCell label="Salesforce success" value={data.system_health.salesforce_success_rate} />
          <HealthCell label="Errors in window" value={data.system_health.errors_in_window} raw />
        </div>
      )}
    </div>
  );
}

function HealthCell({ label, value, raw }) {
  return (
    <div className="card-surface p-4 flex items-baseline justify-between">
      <div className="overline">{label}</div>
      <div className="font-mono text-xl">
        {value === null || value === undefined ? "—" : raw ? value : `${value}%`}
      </div>
    </div>
  );
}

function DrillKpi({ testId, label, value, to, valueClassName = "" }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="card-surface p-4 flex flex-col gap-2 min-h-[120px] hover:shadow-sm transition-shadow relative group"
    >
      <div className="overline">{label}</div>
      <div className={`font-mono text-3xl sm:text-4xl tracking-tight text-zinc-900 ${valueClassName}`}>
        {value}
      </div>
      <div className="text-[10px] text-muted-foreground mono opacity-0 group-hover:opacity-100 transition-opacity">
        view emails →
      </div>
    </Link>
  );
}
